import SessionExpireModal from "components/layout/SessionExpireModal";
import SessionExpiryModel from "components/layout/SessionExpiryModel";
// import SessionExpiryModel from "components/layout/SessionExpiryModel";
import CustomModal from "components/ui-component/CustomModal";
import React, { useCallback, useEffect, useRef } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import LegacyClusterRedirect from "pages/LegacyClusterRedirect";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  updateToken,
  updateTokenStatus,
  useUserData,
} from "store/slices/authSlice";
import { onSetLastRoute, useIsLogin } from "store/slices/commonSlice";
import { RootState } from "store/store";
import { isAllowedClusterId } from "utils/clusterAccess";
import { INVALID_PATH, invalidRouteState } from "utils/invalidRoute";
import { clearSlice, decode } from "utils/HelperFunctions";
import { decodeLegacyUrlValue } from "utils/legacyUrlDecoder";
import {
  getSafePostLoginDestination,
  SafePostLoginDestination,
} from "utils/postLoginRoute";

const SESSION_WARNING_TIMEOUT = 29 * 60 * 1000;
const ACTIVITY_THROTTLE_TIMEOUT = 1000;

const TokenProtectedRoutes = ({ children }: any) => {
  const { clusterId: cluster_id } = useParams();
  const userData = useUserData();
  const { isExpiry, isExpired } = useAppSelector(
    (state: RootState) => state.auth
  );
  const dispatch = useAppDispatch();
  const storeTime = localStorage.getItem(TOKEN_STORAGE_TIME);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityWriteRef = useRef(0);

  const clearWarningTimeout = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  const scheduleSessionWarning = useCallback(() => {
    clearWarningTimeout();

    const storedTokenTime = localStorage.getItem(TOKEN_STORAGE_TIME);
    const tokenTime = storedTokenTime ? parseInt(storedTokenTime, 10) : NaN;

    if (Number.isNaN(tokenTime)) {
      return;
    }

    const timeUntilWarning = tokenTime + SESSION_WARNING_TIMEOUT - Date.now();

    if (timeUntilWarning <= 0) {
      dispatch(updateTokenStatus(true));
      return;
    }

    warningTimeoutRef.current = setTimeout(() => {
      dispatch(updateTokenStatus(true));
    }, timeUntilWarning);
  }, [clearWarningTimeout, dispatch]);

  useEffect(() => {
    if (!userData?.token || isExpiry || isExpired) {
      clearWarningTimeout();
      return;
    }

    const activityEvents = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
    ];

    const handleActivity = () => {
      const currentTime = Date.now();

      if (
        currentTime - lastActivityWriteRef.current <
        ACTIVITY_THROTTLE_TIMEOUT
      ) {
        return;
      }

      lastActivityWriteRef.current = currentTime;
      localStorage.setItem(TOKEN_STORAGE_TIME, `${currentTime}`);
      scheduleSessionWarning();
    };

    scheduleSessionWarning();
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, true);
    });

    return () => {
      clearWarningTimeout();
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity, true);
      });
    };
  }, [
    clearWarningTimeout,
    isExpired,
    isExpiry,
    scheduleSessionWarning,
    userData?.token,
  ]);

  if (storeTime) {
    if (isExpired) {
      return (
        <>
          {children}
          <CustomModal
            signModal={true}
            isSessionModal={true}
            isOpen={isExpired}
            title="Session Timeout"
            className="max-w-[880px] w-full max-h-[800px] h-fit rounded-[10px]"
            onCloseClick={() => {}}
            children={<SessionExpireModal />}
          />
        </>
      );
    } else {
      if (userData?.token && userData?.token !== "") {
        return (
          <>
            {children}
            <CustomModal
              signModal={true}
              isSessionModal={true}
              isOpen={isExpiry}
              title="Session Timeout"
              className="max-w-[880px] w-full max-h-[800px] h-fit rounded-[10px]"
              onCloseClick={() => {}}
              children={<SessionExpiryModel />}
            />
          </>
        );
      } else {
        return <RedirectToLogin clusterId={cluster_id} />;
      }
    }
  } else {
    return <RedirectToLogin clusterId={cluster_id} />;
  }
};

/**
 * Ends the dead session and routes to login. The cleanup dispatches run in an
 * effect rather than during render — dispatching mid-render is what left the
 * login page blank after an expiry redirect.
 */
const RedirectToLogin = ({ clusterId }: { clusterId?: string }) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_TIME);
    clearSlice(dispatch);
  }, [dispatch]);

  return (
    <Navigate
      to={clusterId ? `/${clusterId}/login` : INVALID_PATH}
      replace={true}
      state={clusterId ? undefined : invalidRouteState("not-found")}
    />
  );
};

export const IsLoginProtectedRoutes = ({ children }: any) => {
  const { clusterId: cluster_id } = useParams();
  const isLogin = useIsLogin();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const lastRoute = useAppSelector(
    (state: RootState) => state.common.lastRoute
  );
  let decodedClusterId: string | null = null;

  try {
    decodedClusterId = cluster_id?.startsWith("ci_")
      ? decode(cluster_id, true)
      : decodeLegacyUrlValue(cluster_id);
  } catch {
    decodedClusterId = null;
  }

  const pendingDestination = getSafePostLoginDestination(
    lastRoute,
    decodedClusterId
  );
  const currentDestination = `${location.pathname}${location.search}`;
  const hasReachedPendingDestination =
    pendingDestination?.to === currentDestination;

  useEffect(() => {
    if (
      isLogin &&
      lastRoute &&
      (!pendingDestination || hasReachedPendingDestination)
    ) {
      dispatch(onSetLastRoute(null));
    }
  }, [
    dispatch,
    hasReachedPendingDestination,
    isLogin,
    lastRoute,
    pendingDestination,
  ]);

  if (!isLogin) {
    return <Navigate to={`/${cluster_id}/otp-send`} replace={true} />;
  }

  if (pendingDestination && !hasReachedPendingDestination) {
    return <PostLoginRedirect destination={pendingDestination} />;
  }

  return <>{children}</>;
};

const PostLoginRedirect = ({
  destination,
}: {
  destination: SafePostLoginDestination;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // Consume before navigating so destinations outside the authenticated
    // route tree cannot leave a stale redirect behind.
    dispatch(onSetLastRoute(null));
    navigate(destination.to, {
      replace: true,
      state: destination.state,
    });
  }, [destination, dispatch, navigate]);

  return null;
};

export const TokenNavigation = ({ children }: any) => {
  const { clusterId: cluster_id } = useParams();
  const userData = useUserData();
  if (!userData?.token || userData?.token === "") {
    return <>{children}</>;
  } else {
    return <Navigate to={`/${cluster_id}/dashboard`} replace={true} />;
  }
};

export const CheckClusterId = ({ children }: any) => {
  const { clusterId } = useParams<{ clusterId: string }>();
  const decodedClusterId = clusterId?.startsWith("ci_")
    ? decode(clusterId, true)
    : decodeLegacyUrlValue(clusterId);

  if (!isAllowedClusterId(decodedClusterId)) {
    return (
      <Navigate
        to={INVALID_PATH}
        replace={true}
        state={invalidRouteState("not-found")}
      />
    );
  }

  return <LegacyClusterRedirect>{children}</LegacyClusterRedirect>;
};

export const TokenClearRoute = ({ children }: any) => {
  const { isExpiry } = useAppSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();

  useEffect(() => {
    let intervalId: any;

    if (isExpiry) {
      intervalId = setInterval(() => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(TOKEN_STORAGE_TIME);
        dispatch(updateTokenStatus(false));
        dispatch(updateToken({}));
      }, 5000);
    }

    return () => {
      if (isExpiry) {
        clearInterval(intervalId);
      }
    };
  }, [dispatch, isExpiry]);

  return <>{children}</>;
};

export default TokenProtectedRoutes;
